import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { playMessageSound, playMentionSound, unlockSound } from "../lib/sound";
import { useRealtime } from "./RealtimeContext";

const DataCtx = createContext(null);

export function DataProvider({ children }) {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [ready, setReady] = useState(false);
  const [users, setUsers] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [openTaskId, setOpenTaskId] = useState(null);
  const notifInitRef = useRef(false);
  const seenNotifsRef = useRef(new Set());
  const syncTimerRef = useRef(null);

  const refreshNotifications = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setNotifications(data);
    if (!notifInitRef.current) {
      notifInitRef.current = true;
      seenNotifsRef.current = new Set(data.map((n) => n.id));
      return;
    }
    const fresh = data.filter((n) => !n.read && !seenNotifsRef.current.has(n.id));
    data.forEach((n) => seenNotifsRef.current.add(n.id));
    if (fresh.length > 0) {
      if (fresh.some((n) => n.type === "mention")) playMentionSound();
      else playMessageSound();
    }
  }, []);

  const refreshTasks = useCallback(async () => {
    const { data } = await api.get("/tasks");
    setTasks(data);
  }, []);

  const refreshUsers = useCallback(async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  }, []);

  const bootstrap = useCallback(async () => {
    const { data } = await api.get("/bootstrap");
    setUsers(data.users);
    setSpaces(data.spaces);
    setProjects(data.projects);
    setTasks(data.tasks);
    setNotifications(data.notifications);
    setReady(true);
  }, []);

  useEffect(() => {
    if (user) bootstrap().catch(() => setReady(true));
    else if (user === false) {
      setReady(false);
      setSpaces([]); setProjects([]); setTasks([]); setNotifications([]); setUsers([]);
      notifInitRef.current = false;
      seenNotifsRef.current = new Set();
    }
  }, [user, bootstrap]);

  useEffect(() => {
    if (!user) return undefined;
    unlockSound();
    const scheduleSync = () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => bootstrap().catch(() => {}), 400);
    };
    const unsubscribe = subscribe((evt) => {
      if (evt.type === "data") {
        scheduleSync(); // another device/session changed data — pull latest
        return;
      }
      if (evt.type === "notification") {
        refreshNotifications().catch(() => {});
        if (evt.ntype === "deadline" || evt.ntype === "reminder") return; // toast+sound handled by DeadlineListener
        if (evt.ntype === "mention") playMentionSound();
        else playMessageSound();
      }
    });
    const t2 = setInterval(() => refreshUsers().catch(() => {}), 20000);
    const t3 = setInterval(() => refreshNotifications().catch(() => {}), 120000); // SSE fallback
    return () => {
      unsubscribe();
      clearInterval(t2);
      clearInterval(t3);
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [user, subscribe, bootstrap, refreshNotifications, refreshUsers]);

  const upsertTask = useCallback((item) => setTasks((prev) => {
    const i = prev.findIndex((x) => x.id === item.id);
    if (i === -1) return [...prev, item];
    const next = [...prev]; next[i] = item; return next;
  }), []);
  const upsertSpace = useCallback((item) => setSpaces((prev) => {
    const i = prev.findIndex((x) => x.id === item.id);
    if (i === -1) return [...prev, item];
    const next = [...prev]; next[i] = item; return next;
  }), []);
  const upsertProject = useCallback((item) => setProjects((prev) => {
    const i = prev.findIndex((x) => x.id === item.id);
    if (i === -1) return [...prev, item];
    const next = [...prev]; next[i] = item; return next;
  }), []);
  const removeTask = useCallback((id) => setTasks((p) => p.filter((t) => t.id !== id && t.parent_id !== id)), []);
  const removeSpace = useCallback((id) => setSpaces((p) => p.filter((s) => s.id !== id)), []);
  const removeProject = useCallback((id) => setProjects((p) => p.filter((s) => s.id !== id)), []);

  const value = {
    ready, users, spaces, projects, tasks, notifications,
    openTaskId, setOpenTaskId,
    refreshTasks, refreshNotifications, refreshUsers, bootstrap,
    setUsers, setSpaces, setProjects, setTasks,
    upsertTask, upsertSpace, upsertProject,
    removeTask, removeSpace, removeProject,
  };
  return <DataCtx.Provider value={value}>{children}</DataCtx.Provider>;
}

export const useData = () => useContext(DataCtx);
